export interface CitationLink {
  label: string;
  url: string;
  note?: string;
}

export interface FaqItem {
  id: string;
  question: string;
  shortAnswer: string;
  answer: string;
  sources?: CitationLink[];
}

export const siteMetadata = {
  name: 'VirtualStagePro',
  url: 'https://virtualstagepro.ai',
  logo: 'https://virtualstagepro.ai/logo.webp',
  socialProfiles: [
    'https://www.facebook.com/virtualstageproai',
    'https://www.instagram.com/virtualstagepro.ai/',
  ],
};

export const researchSources = {
  interiorAi: {
    label: 'Interior AI homepage',
    url: 'https://interiorai.com/',
    note: 'Reviewed March 8, 2026.',
  },
  canopyMls: {
    label: 'Canopy MLS virtual staging policy',
    url: 'https://support.canopymls.com/hc/en-us/articles/4405621307287-Virtual-Staging-Photographs',
  },
  narDisclosure: {
    label: 'NAR disclosure guidance',
    url: 'https://www.nar.realtor/magazine/real-estate-news/law-and-ethics/are-you-catfishing-buyers-what-to-know-about-virtual-staging',
  },
};

export const faqItems: FaqItem[] = [
  {
    id: 'what-is-ai-virtual-staging-for-real-estate',
    question: 'What is AI virtual staging for real estate?',
    shortAnswer:
      'AI virtual staging for real estate turns a listing photo into a photorealistic furnished version without physically staging the property.',
    answer:
      'Agents use it to show scale, layout, and buyer fit inside empty rooms while keeping the architecture, windows, flooring, and natural light from the original photo.',
  },
  {
    id: 'how-fast-is-ai-virtual-staging',
    question: 'How fast is AI virtual staging?',
    shortAnswer:
      'VirtualStagePro is built to generate a staged listing image in about 30 seconds for standard rooms.',
    answer:
      'That speed is meant for real listing workflows: upload the source photo, choose a style, then download the finished image in the same working session instead of waiting on a multi-day staging cycle.',
  },
  {
    id: 'how-much-does-virtual-staging-cost',
    question: 'How much does virtual staging cost?',
    shortAnswer:
      'Virtual staging pricing starts at $19 per month on VirtualStagePro, with higher-volume plans that lower the effective per-image cost.',
    answer:
      'That gives agents a subscription-style cost structure instead of coordinating furniture, movers, and reshoots for each property. The exact cost per image depends on plan volume and whether billing is monthly or annual.',
  },
  {
    id: 'do-i-need-professional-real-estate-photos',
    question: 'Do I need professional real estate photos?',
    shortAnswer:
      'No, but the source image still needs clean framing, decent light, and a clear view of the room geometry.',
    answer:
      'Professional photography helps, but many agents start with iPhone-quality photos when the room is bright, level, and unobstructed. The better the base photo, the more believable the staged output will look.',
  },
  {
    id: 'can-you-stage-empty-and-occupied-rooms',
    question: 'Can you stage empty and occupied rooms?',
    shortAnswer:
      'The strongest virtual staging results usually come from empty rooms, while lightly occupied rooms can work if the architecture stays visible and clutter is limited.',
    answer:
      'For occupied rooms, the workflow depends on how much furniture, clutter, and occlusion are in the original shot. If a room is densely occupied, agents usually get cleaner marketing images by shooting it empty or with minimal distractions first.',
  },
  {
    id: 'can-ai-virtual-staging-be-used-for-exterior-photos',
    question: 'Can AI virtual staging be used for exterior photos?',
    shortAnswer:
      'Yes, AI virtual staging can also be used on selected exterior listing photos such as patios, porches, and outdoor living areas.',
    answer:
      'Exterior scenes work best when the main surfaces are clearly visible and the enhancement still reads like a believable marketing image instead of a conceptual redesign. Buyers should still be able to trust what the property actually offers.',
  },
  {
    id: 'how-many-styles-can-i-choose-from',
    question: 'How many styles can I choose from?',
    shortAnswer:
      'VirtualStagePro offers 21 virtual staging styles so agents can match the room to the likely buyer and property type.',
    answer:
      'That matters when the same empty room needs different emotional framing for a downtown condo, a move-up family home, or a coastal second-home listing.',
  },
  {
    id: 'what-image-quality-do-i-get-for-mls-and-marketing',
    question: 'What image quality do I get for MLS and marketing?',
    shortAnswer:
      'VirtualStagePro includes HD downloads on entry plans and 4K downloads on higher-volume plans for MLS and marketing use.',
    answer:
      'That gives teams enough quality to use staged images across listing galleries, brokerage presentations, social crops, and other real-estate marketing surfaces without rebuilding the asset each time.',
  },
  {
    id: 'is-ai-virtual-staging-allowed-on-mls',
    question: 'Is AI virtual staging allowed on MLS?',
    shortAnswer:
      'Often yes, but only if the local MLS rules allow it and the image remains truthful about the property.',
    answer:
      'MLS rules are not universal. Some MLSs permit virtual staging when the edits are limited to furnishings and decor, while others impose extra labeling and disclosure requirements. Agents should check the rules for the specific MLS where the listing will appear before publishing staged photos.',
    sources: [researchSources.canopyMls],
  },
  {
    id: 'do-virtually-staged-photos-need-disclosure',
    question: 'Do virtually staged photos need disclosure?',
    shortAnswer:
      'In many markets, yes. Disclosure is commonly required when furnishings or decor are digitally added to a listing photo.',
    answer:
      'The exact requirement varies by MLS and by local advertising rules, but the safe operational default is to disclose the image clearly and review the MLS policy before syndicating the listing. Disclosure matters most when the edited image could affect buyer expectations.',
    sources: [researchSources.canopyMls, researchSources.narDisclosure],
  },
  {
    id: 'how-is-ai-virtual-staging-different-from-traditional-staging',
    question: 'How is AI virtual staging different from traditional staging?',
    shortAnswer:
      'Traditional staging changes the physical property, while AI virtual staging changes the marketing image.',
    answer:
      'That means AI virtual staging is faster to iterate, easier to test across multiple styles, and simpler to deploy across many listings. Traditional staging can be useful for in-person showings, but it requires scheduling, furniture logistics, and a higher-touch process.',
  },
  {
    id: 'can-brokerages-and-teams-use-ai-virtual-staging-at-scale',
    question: 'Can brokerages and teams use AI virtual staging at scale?',
    shortAnswer:
      'Yes. Brokerage teams use AI virtual staging to standardize listing quality, speed up turnaround, and avoid one-off staging decisions on every property.',
    answer:
      'The operational value is consistency: the same brand standards, style options, and download workflow can be applied across multiple agents, listings, and marketing channels instead of rebuilding the process each time.',
  },
];

export const homepageFaqIds = [
  'what-is-ai-virtual-staging-for-real-estate',
  'how-fast-is-ai-virtual-staging',
  'how-much-does-virtual-staging-cost',
  'do-i-need-professional-real-estate-photos',
  'is-ai-virtual-staging-allowed-on-mls',
  'do-virtually-staged-photos-need-disclosure',
] as const;

const homepageFaqIdSet = new Set<string>(homepageFaqIds);

export const homepageFaqItems = faqItems.filter((item) => homepageFaqIdSet.has(item.id));

export function buildFaqJsonLd(items: FaqItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: `${item.shortAnswer} ${item.answer}`,
      },
    })),
  };
}

export function buildCanonical(path: string): string {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }

  return `${siteMetadata.url}${path}`;
}

export function buildBreadcrumbJsonLd(
  items: Array<{ name: string; path: string }>
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteMetadata.url}${item.path}`,
    })),
  };
}
